import { useEffect, useState, useMemo } from "react";
import PageHeader from "../components/PageHeader";
import TimeFilter from "../components/TimeFilter";
import MonthRangePicker from "../components/MonthRangePicker";
import StatCard from "../components/StatCard";
import DataTable from "../components/DataTable";
import DashboardSection from "../components/DashboardSection";
import ErrorState from "../components/ErrorState";
import DonutCard from "../components/charts/DonutCard";
import BarChartCard from "../components/charts/BarChartCard";
import { SkeletonStatGrid, SkeletonTable, SkeletonChart } from "../components/Skeleton";
import { ShieldIcon, UsersIcon, CheckCircleIcon } from "../components/icons";

import {
  uvToday,
  uvYesterday,
  uvLast7,
  uvLast30,
  uvMonthlyRange
} from "../api/uv.api";

const UV_ORDER = ["Approved", "Rejected", "Rejected & Locked"];

export default function UV() {
  const [filter, setFilter] = useState("today");
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (filter !== "custom") loadData();
  }, [filter]);

  const loadData = async () => {
    setLoading(true);
    setError(null);
    try {
      let res;
      if (filter === "today") res = await uvToday();
      else if (filter === "yesterday") res = await uvYesterday();
      else if (filter === "last7") res = await uvLast7();
      else if (filter === "last30") res = await uvLast30();
      setData(res.data?.summary || res.data);
    } catch (err) {
      setError(err.message || "Request failed");
    } finally {
      setLoading(false);
    }
  };

  const loadCustom = async ({ fromYear, fromMonth, toYear, toMonth }) => {
    setLoading(true);
    setError(null);
    try {
      const res = await uvMonthlyRange({
        start_year: fromYear,
        start_month: fromMonth,
        end_year: toYear,
        end_month: toMonth
      });
      setData(res.data?.data || res.data?.summary || res.data);
    } catch (err) {
      setError(err.message || "Request failed");
    } finally {
      setLoading(false);
    }
  };

  const messageEntries = useMemo(() => {
    if (!data) return [];
    if (Array.isArray(data.message_summary)) {
      return data.message_summary
        .filter((m) => m && m.message && typeof m.count === "number")
        .map((m) => [m.message, m.count]);
    }
    if (typeof data.message_summary === "object" && data.message_summary !== null) {
      return Object.entries(data.message_summary).filter(([_, v]) => typeof v === "number");
    }
    return [];
  }, [data]);

  const officerRows = useMemo(() => {
    if (!data) return [];
    if (Array.isArray(data.officer_summary)) {
      return data.officer_summary.filter((o) => o && o.officer && typeof o.count === "number");
    }
    if (typeof data.officer_summary === "object" && data.officer_summary !== null) {
      return Object.entries(data.officer_summary).map(([k, v]) => ({
        officer: k,
        count: typeof v === "number" ? v : 0
      }));
    }
    return [];
  }, [data]);

  const donutData = useMemo(() => {
    if (messageEntries.length === 0) return null;
    return [...messageEntries]
      .sort((a, b) => UV_ORDER.indexOf(a[0]) - UV_ORDER.indexOf(b[0]))
      .map(([label, value]) => ({ label, value }));
  }, [messageEntries]);

  const shortOfficerName = (email) => {
    if (!email) return "Officer";
    const name = email.split("@")[0].split(".")[0];
    return name.length > 12 ? `${name.slice(0, 12)}…` : name;
  };

  const officerBarData = useMemo(() => {
    if (officerRows.length === 0) return null;
    return [...officerRows]
      .sort((a, b) => b.count - a.count)
      .slice(0, 8)
      .map((o) => ({ label: shortOfficerName(o.officer), value: o.count }));
  }, [officerRows]);

  const totalProcessed = data?.total_uv_processed || 0;

  return (
    <div className="page-container">
      <PageHeader
        title="UV Dashboard"
        subtitle="Verification throughput, approval rates, and officer performance."
      />

      <div className="toolbar">
        <TimeFilter value={filter} onChange={setFilter} />
      </div>
      <MonthRangePicker open={filter === "custom"} onApply={loadCustom} />

      {error ? (
        <ErrorState message={error} onRetry={filter === "custom" ? undefined : loadData} />
      ) : (
        <>
          <DashboardSection title="Verification Overview">
            {loading || !data ? (
              <SkeletonStatGrid count={4} />
            ) : (
              <div className="grid">
                <StatCard
                  title="Total Processed"
                  value={totalProcessed}
                  icon={ShieldIcon}
                  index={0}
                  tone="accent"
                />
                {messageEntries.map(([k, v], i) => (
                  <StatCard
                    key={k}
                    title={k}
                    value={v}
                    icon={/approved/i.test(k) ? CheckCircleIcon : ShieldIcon}
                    index={i + 1}
                  />
                ))}
              </div>
            )}
          </DashboardSection>

          <div className="overview-grid--split">
            {loading || !donutData ? (
              <SkeletonChart />
            ) : (
              <DonutCard
                title="Outcome Split"
                subtitle="Verification decisions for selected range"
                data={donutData}
                centerLabel="approved"
              />
            )}
            {loading || !officerBarData ? (
              <SkeletonChart />
            ) : (
              <BarChartCard
                title="Top Officers By Throughput"
                subtitle="Verifications completed"
                data={officerBarData}
                angledLabels
                formatter={(v) => `${v.toLocaleString("en-IN")} processed`}
              />
            )}
          </div>

          <DashboardSection title="Officer Activity Breakdown">
            {loading || !data ? (
              <SkeletonTable rows={5} />
            ) : (
              <DataTable
                columns={["officer", "count"]}
                data={officerRows}
              />
            )}
          </DashboardSection>
        </>
      )}
    </div>
  );
}
